import { useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router";
import { Toaster } from "sonner";
import { AuthProvider } from "../shared/AuthContext";
import { ThemeProvider } from "../shared/ThemeContext";
import { useAppliedTheme } from "../../hooks/useAppliedTheme";
import { consumePendingRedirect } from "../../utils/redirectState";
import { OfflineBanner } from "../shared/OfflineBanner";
import { useSessionKeepalive } from "../../hooks/useSessionKeepalive";

/**
 * RootLayout — wraps the entire app in providers.
 *
 * Must live INSIDE the router tree (as a route Component) so that
 * all child routes can access AuthContext, ThemeContext, etc.
 *
 * Includes:
 *   - Layer 3 query-param redirect fallback (see App.tsx for the full system)
 *   - Accessibility: skip-to-content link
 *   - OfflineBanner: network connectivity indicator
 */
export function RootLayout() {
  useAppliedTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Session keepalive — extends session sliding window every 30 min
  const hasSession = !!(() => { try { return sessionStorage.getItem("zap_session_token"); } catch { return null; } })();
  useSessionKeepalive(hasSession);

  // ── Layer 3 fallback: if Layers 1-2 failed, catch query params here ──
  useEffect(() => {
    // Check 1: was there a pending redirect that the router still didn't land on?
    const pending = consumePendingRedirect();
    if (pending && location.pathname !== pending) {
      console.warn("[Redirect L3] Pending redirect still unresolved. Navigating to:", pending);
      navigate(pending, { replace: true });
      return;
    }

    // Check 2: are query params somehow still in the URL? (very unlikely but safe)
    try {
      const params = new URLSearchParams(window.location.search);
      const cb = params.get("cb");
      const t = params.get("t");
      const page = params.get("page");

      // ?page= universal redirect (highest priority in L3)
      if (page) {
        const id = params.get("id");
        let target: string;
        if (page === "intake" && id) {
          target = "/intake/" + encodeURIComponent(id);
        } else {
          target = "/" + page.replace(/^\/+/, "");
        }
        console.warn("[Redirect L3] Query param ?page= still present. Navigating to:", target);
        window.history.replaceState(null, "", window.location.pathname + window.location.hash);
        navigate(target, { replace: true });
        return;
      }

      if (cb) {
        const target = "/client-board/" + encodeURIComponent(cb);
        console.warn("[Redirect L3] Query param ?cb= still present. Navigating to:", target);
        // Clean query params from URL
        window.history.replaceState(null, "", window.location.pathname + window.location.hash);
        navigate(target, { replace: true });
        return;
      }
      if (t) {
        const target = "/t/" + encodeURIComponent(t) + "/";
        console.warn("[Redirect L3] Query param ?t= still present. Navigating to:", target);
        window.history.replaceState(null, "", window.location.pathname + window.location.hash);
        navigate(target, { replace: true });
        return;
      }
      const intakeParam = params.get("intake");
      if (intakeParam) {
        const target = "/intake/" + encodeURIComponent(intakeParam);
        console.warn("[Redirect L3] Query param ?intake= still present. Navigating to:", target);
        window.history.replaceState(null, "", window.location.pathname + window.location.hash);
        navigate(target, { replace: true });
        return;
      }
    } catch {
      // ignore
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ThemeProvider>
      <AuthProvider>
        {/* Skip to main content — accessibility */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:right-2 focus:z-[99999] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-bold focus:shadow-lg"
        >
          {"\u05D3\u05DC\u05D2 \u05DC\u05EA\u05D5\u05DB\u05DF \u05D4\u05E8\u05D0\u05E9\u05D9"}
        </a>
        {/* Offline / server-down banner */}
        <OfflineBanner />
        <div id="main-content">
          <Outlet />
        </div>
        <Toaster
          dir="rtl"
          position="bottom-left"
          richColors
          closeButton
          toastOptions={{
            style: { fontFamily: "var(--font-family)" },
            duration: 4000,
          }}
        />
      </AuthProvider>
    </ThemeProvider>
  );
}